import type { EventJobItem, EventJobStatus } from "@/features/events/api/events.api";

/** Topics are all namespaced; the prefix is noise once you are on the admin page. */
const TOPIC_PREFIX = "NEXUS.";
/** Correlation IDs are UUIDs — enough to eyeball, full value lives in `title` and the drawer. */
const CORRELATION_PREVIEW_LENGTH = 13;
/** A zero-width bar reads as "missing data" rather than "very fast". */
const MIN_DURATION_BAR_PERCENT = 3;

export function stripTopicPrefix(topic: string): string {
  return topic.startsWith(TOPIC_PREFIX) ? topic.slice(TOPIC_PREFIX.length) : topic;
}

export function truncateCorrelationId(correlationId: string): string {
  if (correlationId.length <= CORRELATION_PREVIEW_LENGTH) return correlationId;
  return `${correlationId.slice(0, CORRELATION_PREVIEW_LENGTH)}…`;
}

export interface PublishedDisplay {
  time: string;
  date: string;
}

export function getPublishedDisplay(publishedAt: string, locale?: string): PublishedDisplay {
  const parsed = new Date(publishedAt);
  if (Number.isNaN(parsed.getTime())) return { time: "—", date: "" };
  return {
    time: parsed.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
    date: parsed.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
}

/**
 * Bars are scaled against p95 rather than the slowest loaded row: with infinite
 * scroll the observed maximum changes as pages arrive, which would silently
 * rescale every bar already on screen.
 */
export function getDurationBarPercent(
  runDurationMs: number | null,
  scaleMs: number | null,
): number | null {
  if (runDurationMs == null || scaleMs == null || scaleMs <= 0) return null;
  const raw = (runDurationMs / scaleMs) * 100;
  return Math.min(100, Math.max(MIN_DURATION_BAR_PERCENT, Math.round(raw)));
}

export type EventStatusTone = "success" | "error" | "info" | "neutral";

export function getStatusTone(status: EventJobStatus): EventStatusTone {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "FAILED":
      return "error";
    case "PROCESSING":
      return "info";
    default:
      return "neutral";
  }
}

export function isFailedRow(row: Pick<EventJobItem, "status">): boolean {
  return row.status === "FAILED";
}
